import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI, permissionsAPI } from '../../services/api';

// 14/11/2025 - User Access Management Module Phase 5.1.1
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  custom_role_id?: number;  // Added for custom roles
  status: string;
  avatar_url?: string;
  permissions?: string[];  // Added for user permissions
}

// 14/11/2025 - User Access Management Module Phase 5.1.1
// Access/refresh tokens live in httpOnly cookies set by the backend (see
// bakend/app/api/auth.py) — not readable by JS, so they no longer belong in
// this state or in localStorage. `user` is just display/profile info, not a
// credential, so it's fine to keep caching it the same way as before.
interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  permissions: string[];  // Added for permissions
  permissionsLoaded: boolean;  // Track if permissions are loaded
  permissionsLoading: boolean;  // Track if permissions are being fetched
  permissionsError: string | null;  // Track permission fetch errors
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem('user') || 'null') || null,
  loading: false,
  error: null,
  permissions: [],  // 14/11/2025
  permissionsLoaded: false,  // 14/11/2025
  permissionsLoading: false,  // 14/11/2025
  permissionsError: null,  // 14/11/2025
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await authAPI.login({ email, password });

    // The backend already set the auth cookies on this response — only the
    // (non-sensitive) user profile is cached client-side now.
    localStorage.removeItem('user');
    localStorage.setItem('user', JSON.stringify(response.user));

    return response;
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await authAPI.logout(); // clears the auth cookies server-side
    } catch {
      // proceed with local cleanup regardless
    }
    localStorage.removeItem('user');
  }
);

// 14/11/2025 - User Access Management Module Phase 5.1.1
// Fetch user permissions from backend
export const fetchUserPermissions = createAsyncThunk(
  'auth/fetchUserPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await permissionsAPI.getMyPermissions();
      return response.permissions || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    // 14/11/2025 - User Access Management Module Phase 5.1.1
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
      state.permissionsLoaded = true;
      if (state.user) {
        state.user.permissions = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.permissions = [];  // 14/11/2025 - Clear permissions on logout
        state.permissionsLoaded = false;
      })
      // 14/11/2025 - User Access Management Module Phase 5.1.1
      // Handle fetchUserPermissions
      .addCase(fetchUserPermissions.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsError = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.permissions = action.payload;
        state.permissionsLoaded = true;
        // Also update user object with permissions
        if (state.user) {
          state.user.permissions = action.payload;
        }
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsError = action.payload as string;
        state.permissionsLoaded = false;
      });
  },
});

export const { clearError, setUser, setPermissions } = authSlice.actions;
export default authSlice.reducer;